import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Compass, Plus, Archive, Aperture, SignOut, ChartLineUp, ChatCircleText } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "../lib/auth";
import { FeedbackDialog } from "./FeedbackDialog";

export const Sidebar = ({ onNew, recces = [] }) => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { user, logout } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const isArchive = pathname === "/" && params.get("filter") === "archive";
  const isScouting = pathname === "/" && !isArchive;
  const stamp = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex md:flex-col w-[260px] shrink-0 border-r border-rule bg-bone"
    >
      {/* Logo */}
      <div className="px-6 pt-8 pb-6 border-b border-rule">
        <Link
          to="/"
          className="inline-flex items-baseline gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          data-testid="logo-link"
          aria-label="Recce home"
        >
          <Aperture size={22} weight="thin" className="text-ink" />
          <span className="font-serif text-3xl tracking-tight leading-none">Recce</span>
        </Link>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
          reconnaissance · vol.01
        </p>
        {recces.some((r) => r.watching) && (
          <p
            className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-rust"
            data-testid="sidebar-watching-count"
          >
            ◉ {recces.filter((r) => r.watching).length} watching
          </p>
        )}
      </div>

      {/* New */}
      <button
        data-testid="new-recce-cta"
        onClick={onNew}
        className="mx-6 mt-6 flex items-center justify-between px-4 py-3 border border-ink bg-ink text-bone hover:bg-bone hover:text-ink transition-colors"
      >
        <span className="font-mono text-xs uppercase tracking-[0.18em]">New Recce</span>
        <Plus size={14} weight="bold" />
      </button>

      {/* Nav */}
      <nav className="px-6 mt-8 space-y-1">
        <NavLink to="/" icon={Compass} label="Scouting" testid="nav-scouting" active={isScouting} />
        <NavLink
          to="/?filter=archive"
          icon={Archive}
          label="Archive"
          testid="nav-archive"
          active={isArchive}
        />
        {user?.is_owner && (
          <NavLink
            to="/stats"
            icon={ChartLineUp}
            label="Stats"
            testid="nav-stats"
            active={pathname === "/stats"}
          />
        )}
      </nav>

      {/* Project list */}
      <div className="px-6 mt-10 flex-1 overflow-y-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted pb-3 border-b border-rule">
          Recent
        </div>
        <ul className="mt-3 space-y-2">
          {recces.slice(0, 10).map((r) => (
            <li key={r.id}>
              <Link
                to={`/recce/${r.id}`}
                data-testid={`sidebar-recce-${r.id}`}
                className="group flex items-start gap-3 py-2"
              >
                <StatusDot status={r.status} />
                <div className="min-w-0">
                  <div className="font-serif text-base leading-tight truncate group-hover:underline">
                    {r.title || "Untitled"}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                    {r.medium} · {r.status}
                  </div>
                </div>
              </Link>
            </li>
          ))}
          {recces.length === 0 && (
            <li className="font-mono text-[11px] text-ink-muted">No recces yet.</li>
          )}
        </ul>
      </div>

      {/* Footer — user + logout */}
      <div className="px-6 py-5 border-t border-rule tape">
        {user && (
          <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name || user.email}
                className="w-8 h-8 border border-ink object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div className="w-8 h-8 border border-ink bg-bone-deep flex items-center justify-center font-serif text-base">
                {(user.name || user.email).slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-serif text-sm leading-tight truncate">
                {user.name || "—"}
              </div>
              <div className="font-mono text-[10px] text-ink-muted truncate">
                {user.email}
              </div>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-ink-muted hover:text-rust"
              title="sign out"
              data-testid="sidebar-logout-btn"
              aria-label="sign out"
            >
              <SignOut size={14} />
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          data-testid="sidebar-feedback-btn"
          className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted hover:text-rust mb-2"
        >
          <ChatCircleText size={12} weight="thin" />
          <span className="underline-offset-2 group-hover:underline">send feedback</span>
        </button>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
          {stamp}
        </div>
        <div className="font-mono text-[10px] text-ink-muted mt-1">
          agent: claude · sonnet 4.5
        </div>
      </div>
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </aside>
  );
};

const NavLink = ({ to, active, icon: Icon, label, testid }) => {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 px-2 py-2 font-mono text-xs uppercase tracking-[0.18em] border-l-2 ${
        active
          ? "border-ink text-ink"
          : "border-transparent text-ink-muted hover:text-ink"
      }`}
    >
      <Icon size={16} weight="thin" />
      {label}
    </Link>
  );
};

const StatusDot = ({ status }) => {
  const color =
    status === "ready"
      ? "bg-sage"
      : status === "scouting"
      ? "bg-rust animate-pulse"
      : status === "error"
      ? "bg-red-500"
      : "bg-rule";
  return <span className={`mt-2 h-1.5 w-1.5 rounded-full ${color}`} />;
};
